/** @shell standard @header bar */
import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SectionList,
  ScrollView,
  Image,
  RefreshControl,
  Platform,
  Pressable,
  type StyleProp,
  type ViewStyle,
  type LayoutChangeEvent,
  type SectionListProps,
  useWindowDimensions,
} from 'react-native';
import Animated, {
  Extrapolation,
  FadeInDown,
  FadeOutUp,
  interpolate,
  useAnimatedReaction,
  useSharedValue,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useAuthStore } from '../shared/auth/store';
import { resolveWebLayoutWidth } from '../shared/platform/webLayout';
import { useAuthNavigation, useExitOrGoBack } from '../shared/navigation/useAuthNavigation';
import { Clickable } from '../components/common/Clickable';
import { Clickable2 } from '../components/common/Clickable2';
import { SvgIcon } from '../components/common/SvgIcon';
import { ScreenHeader, useScreenHeaderScroll } from '../components/common/ScreenHeader';
import { HeaderIconButton } from '../components/common/HeaderBackButton';
import { appTheme, useThemeColors, type ThemeColors } from '../shared/theme';
import { hexToRgba } from '../shared/theme/colorUtils';
import { AppLinearGradient as LinearGradient } from '../components/ui/AppLinearGradient';
import { useNavigation } from '@react-navigation/native';
import { useSubscriptionPlans } from '../data/domains/subscription';
import { LoadingState } from '../components/common/LoadingState';
import { ErrorState } from '../components/common/ErrorState';
import { EmptyState } from '../components/common/EmptyState';
import { formatCurrency } from '../shared/utils/currencyUtils';
import { runAfterInteractions } from '../shared/utils/defer';
import {
  applyV3CatalogPlanDayOffFields,
  buildPlanItemsFromCategoryDayEntry,
  buildSubscriptionListViewModels,
  computeCategoryBannerMetrics,
  computeTagBannerMetrics,
  firstPlanImageForTagSlide,
  firstPlanImageFromCategoryDaysItem,
  firstPlanImageFromCustomCategory,
  getUniqueSortedDaysFromSection,
  getPlanDisplayPrice,
  hasActiveSubscriptionOffer,
  processSubscriptionData,
  resolvePlanOfferPricing,
  setSelectedSubscription,
  shouldOfferFreeDaysModal,
  sortByDisplayOrder,
  type BannerSlideMetrics,
  type CategoryDaysData,
  type CategoryDaysItem,
  type CategoryDayEntry,
  type PlanItem,
  type PlanSection,
} from '../data/domains/subscription';
import type { PlanDayOffOption } from '../data/domains/checkout';
import { useTranslation, getApiText } from '../shared/i18n';
import { useRtl } from '../shared/i18n/useRtl';
import {
  androidAppHeaderContentHeight,
  androidHeaderTopInset,
} from '../shared/theme/sharedStyles';
import { createSubscriptionListStyles, type SubscriptionListStyles, SLIDE_CARD_HEIGHT, SLIDE_STACK_OFFSET, SLIDE_WIDTH, SUBSCRIPTION_CAROUSEL_CHROME_EXTRA } from './components/subscriptionListStyles';
import { BottomSheetPopup } from '../components/common/BottomSheetPopup';
import { useSubscriptionListStructure, useSubscriptionListStyle } from '../data/domains/config';
import { useApiConfig, getOrganization, getSettings } from '../data/domains/config';
import { resolveAttachmentUri } from '../shared/utils/resolveMediaUri';
import { useAuthSheets } from '../shared/auth/authSheetContext';
import { StackScreenShell } from '../shared/theme/studio/stackScreenShell';
import { SubscriptionCatalogPlanCard } from './components/SubscriptionCatalogPlanCard';
import { SubscriptionCatalogCategoryCard } from './components/SubscriptionCatalogCategoryCard';
import { SubscriptionCatalogDayTile } from './components/SubscriptionCatalogDayTile';
import {
  CategorySlider,
  MERGED_CAROUSEL_COLLAPSE_DISTANCE,
  MERGED_CAROUSEL_EXPANDED_HEIGHT,
  MERGED_DAY_PILL_BAR_HEIGHT,
} from './components/SubscriptionListCategorySlider';
import { BannerPopupModal, CategoryPlanModal, CategoryFlowDayPlansModal } from './components/SubscriptionListPlanModals';
import { CustomPlanBuilder } from './components/SubscriptionListCustomPlanBuilder';
import type { TagChipItem, CustomCheckoutPayload } from './components/subscriptionListTypes';
export type { TagChipItem, CustomCheckoutPayload } from './components/subscriptionListTypes';

import { useShowCustomPlansEnabled } from '../data/domains/config';
import { SubscriptionListPremiumScreen } from './SubscriptionListPremiumScreen';
import { useRoute, type RouteProp } from '@react-navigation/native';
import type { RootStackParamList } from '../navigation/types';
import { setGuestExplorePlan } from '../data/domains/guest';

export type { PlanItem, PlanSection } from '../data/domains/subscription';

const ANDROID_FOOTER_TEXT = Platform.OS === 'android' ? { includeFontPadding: false } : {};
/** Reanimated drops SectionList generics — re-assert PlanItem / PlanSection for render callbacks. */
const AnimatedSectionList = Animated.createAnimatedComponent(SectionList) as unknown as React.ComponentType<
  SectionListProps<PlanItem, PlanSection>
>;

/** Stacked carousel paginator + shell padding (see StackedCarousel minHeight). */
const MERGED_LIST_STAGGER_MS = 48;
const MERGED_LIST_STAGGER_CAP = 6;
const MERGED_LIST_ENTER_MS = 320;
const MERGED_LIST_EXIT_MS = 180;

function mergedListEntering(index: number) {
  return FadeInDown.delay(Math.min(index, MERGED_LIST_STAGGER_CAP) * MERGED_LIST_STAGGER_MS).duration(
    MERGED_LIST_ENTER_MS,
  );
}

function mergedListExiting(index: number) {
  return FadeOutUp.delay(Math.min(index, MERGED_LIST_STAGGER_CAP) * 28).duration(MERGED_LIST_EXIT_MS);
}

export const SubscriptionListScreen: React.FC = () => {
  const subscription_list_structure = useSubscriptionListStructure();
  if (subscription_list_structure === 'premium') {
    return <SubscriptionListPremiumScreen />;
  }
  return <SubscriptionListCatalogScreen />;
};

const SubscriptionListCatalogScreen: React.FC = () => {
  const navigation = useAuthNavigation();
  const route = useRoute<RouteProp<RootStackParamList, 'SubscriptionList'>>();
  const guest_explore = (route.params as { guestExplore?: boolean } | undefined)?.guestExplore === true;
  const on_back_exit = useExitOrGoBack();
  const { openRegister } = useAuthSheets();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const { chevronNextAlongAxis, flipRowStyle } = useRtl();
  const user = useAuthStore(s => s.user);
  const { organization } = useApiConfig();
  const subscription_list_structure = useSubscriptionListStructure();
  const subscription_list_style = useSubscriptionListStyle();
  const show_custom_tab = useShowCustomPlansEnabled();
  const { data: plans, isLoading, error, refetch } = useSubscriptionPlans();

  const themeColors = useThemeColors() ?? appTheme.colors;
  const { width: window_width, height: window_height } = useWindowDimensions();
  const layout_width = resolveWebLayoutWidth(window_width);
  const styles = useMemo(
    () => createSubscriptionListStyles(themeColors, layout_width),
    [themeColors, layout_width],
  );
  const { scrollY, onScroll } = useScreenHeaderScroll();

  /**
   * Merged chrome collapse is scroll-linked:
   * - y=0 → expanded; list top sits under expanded slider bottom
   * - y=COLLAPSE_DISTANCE → collapsed; list top sits under collapsed slider bottom
   * - Filter changes while collapsed snap to COLLAPSE_DISTANCE (new list under collapsed edge)
   * - Expand only via manual scroll back toward 0
   * - `merged_collapse_hold` freezes progress=1 during content swaps so scroll clamps can't flash expand
   */
  const merged_carousel_collapse_progress = useSharedValue(0);
  const merged_collapse_hold = useSharedValue(0);
  const merged_scroll_ref = useRef<Animated.ScrollView>(null);
  const merged_filter_key_ref = useRef('');
  const merged_restore_collapsed_ref = useRef(false);
  const merged_plan_press_lock_ref = useRef(0);
  const [merged_list_render_items, setMergedListRenderItems] = useState<PlanItem[]>([]);
  const [merged_scroll_viewport_h, setMergedScrollViewportH] = useState(0);
  const [merged_list_enter_key, setMergedListEnterKey] = useState('');

  useAnimatedReaction(
    () => scrollY.value,
    y => {
      'worklet';
      if (merged_collapse_hold.value === 1) {
        merged_carousel_collapse_progress.value = 1;
        return;
      }
      merged_carousel_collapse_progress.value = interpolate(
        y,
        [0, MERGED_CAROUSEL_COLLAPSE_DISTANCE],
        [0, 1],
        Extrapolation.CLAMP,
      );
    },
    [scrollY],
  );

  const snapMergedScrollCollapsed = useCallback(() => {
    const y = MERGED_CAROUSEL_COLLAPSE_DISTANCE;
    scrollY.value = y;
    merged_carousel_collapse_progress.value = 1;
    merged_scroll_ref.current?.scrollTo({ y, animated: false });
  }, [merged_carousel_collapse_progress, scrollY]);

  /** Hold collapsed chrome + pin scroll under collapsed edge before list content swaps. */
  const prepareMergedFilterTransition = useCallback(() => {
    const needs_hold =
      merged_carousel_collapse_progress.value > 0.05 || scrollY.value > 8;
    merged_restore_collapsed_ref.current = needs_hold;
    if (needs_hold) {
      merged_collapse_hold.value = 1;
      merged_carousel_collapse_progress.value = 1;
      snapMergedScrollCollapsed();
    }
  }, [
    merged_carousel_collapse_progress,
    merged_collapse_hold,
    scrollY,
    snapMergedScrollCollapsed,
  ]);

  const releaseMergedCollapseHold = useCallback(() => {
    const restore = merged_restore_collapsed_ref.current;
    if (restore) {
      snapMergedScrollCollapsed();
    }
    requestAnimationFrame(() => {
      if (restore) {
        snapMergedScrollCollapsed();
      }
      requestAnimationFrame(() => {
        if (restore) {
          snapMergedScrollCollapsed();
        }
        merged_collapse_hold.value = 0;
        merged_restore_collapsed_ref.current = false;
      });
    });
  }, [merged_collapse_hold, snapMergedScrollCollapsed]);

  const onMergedScrollLayout = useCallback((event: LayoutChangeEvent) => {
    const h = Math.ceil(event.nativeEvent.layout.height);
    setMergedScrollViewportH(prev => (prev === h ? prev : h));
  }, []);

  const onMergedContentSizeChange = useCallback(() => {
    if (!merged_restore_collapsed_ref.current) {
      return;
    }
    snapMergedScrollCollapsed();
  }, [snapMergedScrollCollapsed]);
  const [selectedTagId, setSelectedTagId] = useState<string | null>('all-0');
  const [refreshing, setRefreshing] = useState(false);
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);
  const [showFreeDaysModal, setShowFreeDaysModal] = useState(false);
  const [selectedPlanWithFreeDays, setSelectedPlanWithFreeDays] = useState<any>(null);
  const [includeFreeDays, setIncludeFreeDays] = useState(false);
  const [showBannerPopup, setShowBannerPopup] = useState(false);
  const [bannerData, setBannerData] = useState<TagChipItem | CategoryDaysItem | null>(null);
  const [bannerMetrics, setBannerMetrics] = useState<BannerSlideMetrics | null>(null);
  const [banner_fallback_image_uri, setBannerFallbackImageUri] = useState<string | null>(null);
  const [showCategoryPopup, setShowCategoryPopup] = useState(false);
  const [categoryPopupSection, setCategoryPopupSection] = useState<PlanSection | null>(null);
  const [selectedDayForPopup, setSelectedDayForPopup] = useState<number | null>(null);
  const [selectedCategoryFlowId, setSelectedCategoryFlowId] = useState<string | number | null>(null);
  const [showCategoryFlowDayModal, setShowCategoryFlowDayModal] = useState(false);
  const [categoryFlowDayEntry, setCategoryFlowDayEntry] = useState<CategoryDayEntry | null>(null);
  const [categoryFlowCategoryTitle, setCategoryFlowCategoryTitle] = useState<string>('');
  /** Merged structure: selected day pill + selected plan for checkout footer. */
  const [selectedMergedDay, setSelectedMergedDay] = useState<number | null>(null);
  const [selectedMergedPlan, setSelectedMergedPlan] = useState<PlanItem | null>(null);
  /** Plan selected while a category sheet is open — navigate only after `onDismissComplete`. */
  const pending_checkout_plan_ref = useRef<PlanItem | null>(null);

  const [subscription_type, setSubscriptionType] = useState<'regular' | 'custom'>('regular');
  const [customCategoryId, setCustomCategoryId] = useState<string | number | null>(null);
  const [selectedParticularIndex, setSelectedParticularIndex] = useState<number>(0);
  const [meal_counter, setMealCounter] = useState(0);
  const [snack_counter, setSnackCounter] = useState(0);
  const [protein_carbs_array, setProteinCarbsArray] = useState<Array<{ protein: string; carbs: string }>>([]);
  const [measured_filter_chrome_h, setMeasuredFilterChromeH] = useState(0);
  const isNewCustomer = useMemo(() => !(user as any)?.has_active_subscription && !(user as any)?.has_subscription_history, [user]);
  const is_logged_in = !!user;

  const { regular_plans: regularPlans, custom_plans: customPlans, has_custom_plans: hasCustomPlans } = useMemo(() => processSubscriptionData(Array.isArray(plans) ? plans : []), [plans]);
  const use_category_days_hierarchy =
    subscription_list_structure === 'category' || subscription_list_structure === 'merged';

  const use_hierarchy_effective = use_category_days_hierarchy;

  /** Tag / category metadata — independent of selected tag so the carousel `data` stays stable across swipes. */
  const catalog_meta = useMemo(
    () => buildSubscriptionListViewModels(regularPlans, null, use_hierarchy_effective),
    [regularPlans, use_hierarchy_effective],
  );
  const restructuredData = catalog_meta.restructuredData;
  const categoryDaysData = catalog_meta.categoryDaysData;

  const { sections } = useMemo(
    () => buildSubscriptionListViewModels(regularPlans, selectedTagId, use_hierarchy_effective),
    [regularPlans, selectedTagId, use_hierarchy_effective],
  );

  const tags = useMemo((): TagChipItem[] => {
    const list: TagChipItem[] = restructuredData?.metadata?.tags
      ? restructuredData.metadata.tags.map((tag: any, i: number) => ({
          id: tag.tag_id ?? `tag-${i}`,
          name: tag.tag_name ?? t('subscriptions.plan'),
          attachment: resolveAttachmentUri(tag.tag_attachment) ?? resolveAttachmentUri((tag as { attachment?: unknown }).attachment),
          description: typeof (tag as any).description === 'string' ? (tag as any).description : null,
        }))
      : [];
    if (list.length > 0) {
      list.unshift({ id: 'all-0', name: t('subscriptions.all') });
    }
    return list;
  }, [restructuredData, t]);

  useEffect(() => {
    if (isNewCustomer && !isLoading && plans && regularPlans.length > 0) {
      // setShowWelcomeModal(true);
    }
  }, [isNewCustomer, isLoading, plans, regularPlans.length]);

  useEffect(() => {
    if (categoryDaysData?.categories?.length && selectedCategoryFlowId == null) {
      const first = categoryDaysData.categories[0];
      setSelectedCategoryFlowId(first?.id ?? first?.title ?? null);
    }
  }, [categoryDaysData, selectedCategoryFlowId]);

  useEffect(() => {
    if (regularPlans.length === 0 && hasCustomPlans && !!show_custom_tab) {
      setSubscriptionType('custom');
    }
  }, [regularPlans.length, hasCustomPlans, show_custom_tab]);

  useEffect(() => {
    if (!show_custom_tab && subscription_type === 'custom') {
      setSubscriptionType('regular');
    }
  }, [show_custom_tab, subscription_type]);

  useEffect(() => {
    if (subscription_type === 'custom' && !!show_custom_tab) {
      setMeasuredFilterChromeH(0);
    }
  }, [subscription_type, show_custom_tab]);

  useEffect(() => {
    if (hasCustomPlans && customPlans.length > 0 && customCategoryId == null) {
      setCustomCategoryId(customPlans[0].id ?? customPlans[0].title ?? null);
      setSelectedParticularIndex(0);
    }
  }, [hasCustomPlans, customPlans, customCategoryId]);

  useEffect(() => {
    if (!hasCustomPlans || customPlans.length === 0) {
      return;
    }
    const cat = customPlans.find((c: any) => String(c.id ?? c.title) === String(customCategoryId)) ?? customPlans[0];
    const parts = sortByDisplayOrder(cat?.particulars ?? []);
    const part: any = parts[selectedParticularIndex] ?? parts[0];
    if (!part) {
      return;
    }
    const min_meals = Math.max(0, Number(part.minimum_meals ?? 0));
    const min_snacks = Math.max(0, Number(part.minimum_snacks ?? 0));
    const price_protein = part.price_protein && typeof part.price_protein === 'object' ? (part.price_protein as Record<string, number>) : {};
    const price_carbs = part.price_carbs && typeof part.price_carbs === 'object' ? (part.price_carbs as Record<string, number>) : {};
    const protein_keys = Object.keys(price_protein);
    const carbs_keys = Object.keys(price_carbs);
    const first_protein = protein_keys[0] ?? '';
    const first_carbs = carbs_keys[0] ?? '';
    setMealCounter(min_meals);
    setSnackCounter(min_snacks);
    setProteinCarbsArray(Array.from({ length: min_meals }, () => ({ protein: first_protein, carbs: first_carbs })));
  }, [hasCustomPlans, customPlans, customCategoryId, selectedParticularIndex]);

  const handleSelectPlan = useCallback(
    (item: PlanItem) => {
      const plan = item.plan;
      const show_free_days_modal = shouldOfferFreeDaysModal(plan);

      const subscription_category_id =
        plan.subscription_category_id ?? plan.category_id ?? (plan as any).subscription_category?.id ?? null;

      const plan_base = applyV3CatalogPlanDayOffFields(plan as Record<string, unknown>);

      const planForCheckout = {
        ...plan_base,
        days: item.days,
        id: plan.id ?? plan.subscription_id,
        subscription_category_id,
        category_id: subscription_category_id ?? plan.category_id,
        subscription_id: plan.subscription_id ?? plan.id,
        celebrity_id: plan.celebrity_id ?? 0,
        number_of_meals: plan.number_of_meals ?? plan.total_meals ?? 0,
        number_of_snacks: plan.number_of_snacks ?? plan.total_snacks ?? 0,
        customized_meals: plan.customized_meals ?? (plan as any).is_customized ?? [],
        isCustomAllowed: plan.isCustomAllowed === true || (plan as any).is_customized === true,
      };

      const goToCheckoutOrRegister = async (payload: { plan: any; free_days_selected?: number }) => {
        if (!is_logged_in && guest_explore) {
          await setGuestExplorePlan(payload.plan as Record<string, unknown>);
          navigation.navigate('GuestMenuPreview' as any);
          return;
        }
        await setSelectedSubscription(payload.plan);
        if (!is_logged_in) {
          openRegister({ subData: payload.plan });
        } else {
          navigation.navigate('Checkout' as any, { plan: payload.plan, free_days_selected: payload.free_days_selected ?? 0 });
        }
      };

      if (show_free_days_modal) {
        // Avoid stacking sheets/popups on top of each other.
        setShowBannerPopup(false);
        setBannerData(null);
        setBannerMetrics(null);
        setBannerFallbackImageUri(null);
        setShowCategoryPopup(false);
        setShowCategoryFlowDayModal(false);
        setShowWelcomeModal(false);
        setSelectedPlanWithFreeDays(planForCheckout);
        setIncludeFreeDays(false);
        setShowFreeDaysModal(true);
      } else {
        goToCheckoutOrRegister({ plan: planForCheckout, free_days_selected: 0 });
      }
    },
    [navigation, is_logged_in, openRegister, guest_explore],
  );

  const handleConfirmFreeDays = useCallback(async () => {
    if (!selectedPlanWithFreeDays) {
      return;
    }
    const free_add = includeFreeDays ? Number(selectedPlanWithFreeDays.free_days ?? 0) : 0;
    const planWithFreeDays = {
      ...selectedPlanWithFreeDays,
      free_days_selected: free_add,
    };
    setShowFreeDaysModal(false);
    setSelectedPlanWithFreeDays(null);
    if (!is_logged_in && guest_explore) {
      await setGuestExplorePlan(planWithFreeDays as Record<string, unknown>);
      navigation.navigate('GuestMenuPreview' as any);
      return;
    }
    if (!is_logged_in) {
      await setSelectedSubscription(planWithFreeDays);
      openRegister({ subData: planWithFreeDays });
    } else {
      navigation.navigate('Checkout' as any, { plan: planWithFreeDays, free_days_selected: free_add });
    }
  }, [selectedPlanWithFreeDays, includeFreeDays, navigation, is_logged_in, openRegister, guest_explore]);

  const close_category_plan_modal = useCallback(() => {
    pending_checkout_plan_ref.current = null;
    setShowCategoryPopup(false);
  }, []);

  const close_category_flow_day_modal = useCallback(() => {
    pending_checkout_plan_ref.current = null;
    setShowCategoryFlowDayModal(false);
  }, []);

  const handleSelectPlanFromCategoryModal = useCallback((item: PlanItem) => {
    pending_checkout_plan_ref.current = item;
    setShowCategoryPopup(false);
  }, []);

  const handleSelectPlanFromCategoryFlowModal = useCallback((item: PlanItem) => {
    pending_checkout_plan_ref.current = item;
    setShowCategoryFlowDayModal(false);
  }, []);

  const on_category_plan_dismiss_complete = useCallback(() => {
    setCategoryPopupSection(null);
    setSelectedDayForPopup(null);
    const pending = pending_checkout_plan_ref.current;
    pending_checkout_plan_ref.current = null;
    if (pending) {
      runAfterInteractions(() => handleSelectPlan(pending));
    }
  }, [handleSelectPlan]);

  const on_category_flow_day_dismiss_complete = useCallback(() => {
    setCategoryFlowDayEntry(null);
    setCategoryFlowCategoryTitle('');
    const pending = pending_checkout_plan_ref.current;
    pending_checkout_plan_ref.current = null;
    if (pending) {
      runAfterInteractions(() => handleSelectPlan(pending));
    }
  }, [handleSelectPlan]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    refetch().finally(() => setRefreshing(false));
  }, [refetch]);

  const openCategoryPopup = useCallback((section: PlanSection) => {
    setCategoryPopupSection(section);
    const days = getUniqueSortedDaysFromSection(section);
    setSelectedDayForPopup(days.length > 0 ? days[0] : null);
    setShowCategoryPopup(true);
  }, []);

  const on_filter_chrome_layout = useCallback(
    (event: LayoutChangeEvent) => {
      // Merged chrome height is animated via collapseProgress; measuring it causes
      // per-frame setState and remounts the slider (collapsed↔expanded flicker).
      if (subscription_list_structure === 'merged') {
        return;
      }
      const h = Math.ceil(event.nativeEvent.layout.height);
      setMeasuredFilterChromeH(prev => (prev === h ? prev : h));
    },
    [subscription_list_structure],
  );

  const renderSectionHeader = useCallback(
    ({ section }: { section: PlanSection }) => (
      <View style={styles.sectionHeader}>
        <Pressable
          style={({ pressed }) => [styles.sectionHeaderPressable, pressed && Platform.OS === 'ios' ? { opacity: 0.92 } : null]}
          onPress={() => openCategoryPopup(section)}
          accessibilityRole="button">
          <Text style={styles.sectionHeaderEyebrow}>{t('subscriptions.plans')}</Text>
          <View style={styles.sectionHeaderPressableInner}>
            <Text style={styles.sectionHeaderTitle}>
              {section.title === 'Plans' ? t('subscriptions.plans') : getApiText(section as unknown as Record<string, unknown>, 'title', 'arabic_title') || section.title}
            </Text>
            <SvgIcon name={chevronNextAlongAxis} size={18} color={themeColors.textSecondary} />
          </View>
        </Pressable>
      </View>
    ),
    [t, openCategoryPopup, themeColors, styles, chevronNextAlongAxis],
  );

  const renderItem = useCallback(
    ({ item }: { item: PlanItem }) => (
      <SubscriptionCatalogPlanCard item={item} colors={themeColors} styles={styles} onSelect={() => handleSelectPlan(item)} />
    ),
    [handleSelectPlan, themeColors, styles],
  );

  const listHeader = useMemo(() => <View style={styles.listHeader} />, [styles]);

  const handleShowBanner = useCallback(
    (item: TagChipItem | CategoryDaysItem) => {
      const is_category_item = Array.isArray((item as CategoryDaysItem).days);
      const metrics = is_category_item
        ? computeCategoryBannerMetrics(item as CategoryDaysItem)
        : computeTagBannerMetrics(String((item as TagChipItem).id), restructuredData, regularPlans);
      const fallback_image = is_category_item
        ? resolveAttachmentUri((item as CategoryDaysItem).attachment) ?? firstPlanImageFromCategoryDaysItem(item as CategoryDaysItem)
        : resolveAttachmentUri((item as TagChipItem).attachment) ??
          firstPlanImageForTagSlide(String((item as TagChipItem).id), restructuredData, regularPlans);
      setShowFreeDaysModal(false);
      setShowCategoryPopup(false);
      setShowCategoryFlowDayModal(false);
      setShowWelcomeModal(false);
      setBannerData(item);
      setBannerMetrics(metrics);
      setBannerFallbackImageUri(fallback_image);
      setShowBannerPopup(true);
    },
    [restructuredData, regularPlans],
  );

  /** Category flow when `subscription_list_structure === 'category'` and API returns category/days hierarchy. */
  const is_category_flow = useMemo(() => {
    const n_cats = categoryDaysData?.categories?.length ?? 0;
    return Boolean(subscription_list_structure === 'category' && use_hierarchy_effective && n_cats > 0);
  }, [subscription_list_structure, use_hierarchy_effective, categoryDaysData?.categories?.length]);

  /** Merged flow: category carousel + day pills + inline plan list + checkout footer. */
  const is_merged_flow = useMemo(() => {
    const n_cats = categoryDaysData?.categories?.length ?? 0;
    return Boolean(subscription_list_structure === 'merged' && n_cats > 0);
  }, [subscription_list_structure, categoryDaysData?.categories?.length]);

  const category_flow_categories = categoryDaysData?.categories ?? [];
  const selected_category_flow: CategoryDaysItem | null = useMemo(
    () => category_flow_categories.find((c: CategoryDaysItem) => String(c.id ?? c.title) === String(selectedCategoryFlowId)) ?? category_flow_categories[0] ?? null,
    [category_flow_categories, selectedCategoryFlowId],
  );

  const merged_category_title = useMemo(() => {
    if (!selected_category_flow) {
      return '';
    }
    return (
      getApiText(selected_category_flow as unknown as Record<string, unknown>, 'title', 'arabic_title') ||
      selected_category_flow.title ||
      ''
    );
  }, [selected_category_flow]);

  const merged_day_options = useMemo(() => {
    if (!selected_category_flow?.days?.length) {
      return [] as number[];
    }
    return (selected_category_flow.days ?? [])
      .map((day_entry: CategoryDayEntry) => Number(day_entry.day ?? day_entry.type_key ?? 0))
      .filter((day_val: number) => day_val > 0);
  }, [selected_category_flow]);

  const merged_selected_day_entry = useMemo(() => {
    if (!selected_category_flow?.days?.length || selectedMergedDay == null) {
      return null;
    }
    return (
      selected_category_flow.days.find(
        (day_entry: CategoryDayEntry) => Number(day_entry.day ?? day_entry.type_key ?? 0) === selectedMergedDay,
      ) ?? null
    );
  }, [selected_category_flow, selectedMergedDay]);

  const merged_plan_items = useMemo(
    () => buildPlanItemsFromCategoryDayEntry(merged_selected_day_entry, merged_category_title || t('subscriptions.plan')),
    [merged_selected_day_entry, merged_category_title, t],
  );

  const merged_filter_key = `${String(selectedCategoryFlowId ?? '')}:${selectedMergedDay ?? ''}`;

  useEffect(() => {
    if (!is_merged_flow) {
      merged_filter_key_ref.current = '';
      setMergedListRenderItems([]);
      setMergedListEnterKey('');
      merged_collapse_hold.value = 0;
      merged_restore_collapsed_ref.current = false;
      return;
    }
    if (merged_filter_key_ref.current === '') {
      merged_filter_key_ref.current = merged_filter_key;
      setMergedListRenderItems(merged_plan_items);
      setMergedListEnterKey(merged_filter_key);
      return;
    }
    if (merged_filter_key_ref.current === merged_filter_key) {
      setMergedListRenderItems(prev => {
        if (
          prev.length === merged_plan_items.length &&
          prev.every((row, index) => row.key === merged_plan_items[index]?.key)
        ) {
          return prev;
        }
        return merged_plan_items;
      });
      return;
    }
    merged_filter_key_ref.current = merged_filter_key;
    setMergedListRenderItems(merged_plan_items);
    setMergedListEnterKey(merged_filter_key);
    if (merged_restore_collapsed_ref.current) {
      snapMergedScrollCollapsed();
    }
    releaseMergedCollapseHold();
  }, [
    is_merged_flow,
    merged_filter_key,
    merged_plan_items,
    merged_collapse_hold,
    releaseMergedCollapseHold,
    snapMergedScrollCollapsed,
  ]);

  const selectMergedCategory = useCallback(
    (id: string | number | null) => {
      if (String(id) === String(selectedCategoryFlowId)) {
        return;
      }
      const cat =
        category_flow_categories.find((c: CategoryDaysItem) => String(c.id ?? c.title) === String(id)) ??
        category_flow_categories[0];
      const next_days = (cat?.days ?? [])
        .map((day_entry: CategoryDayEntry) => Number(day_entry.day ?? day_entry.type_key ?? 0))
        .filter((day_val: number) => day_val > 0);
      prepareMergedFilterTransition();
      setSelectedCategoryFlowId(id);
      setSelectedMergedDay(next_days[0] ?? null);
      setSelectedMergedPlan(null);
    },
    [category_flow_categories, prepareMergedFilterTransition, selectedCategoryFlowId],
  );

  const selectMergedDay = useCallback(
    (day_val: number) => {
      if (selectedMergedDay === day_val) {
        return;
      }
      prepareMergedFilterTransition();
      setSelectedMergedDay(day_val);
      setSelectedMergedPlan(null);
    },
    [selectedMergedDay, prepareMergedFilterTransition],
  );

  /** Tap selects; tap again deselects. Ignore duplicate presses from gesture remounts. */
  const selectMergedPlan = useCallback((item: PlanItem) => {
    const now = Date.now();
    if (now - merged_plan_press_lock_ref.current < 400) {
      return;
    }
    merged_plan_press_lock_ref.current = now;
    setSelectedMergedPlan(prev => (prev?.key === item.key ? null : item));
  }, []);

  const proceedMergedCheckout = useCallback(() => {
    if (selectedMergedPlan == null) {
      return;
    }
    const fresh_plan =
      merged_plan_items.find(row => row.key === selectedMergedPlan.key) ?? selectedMergedPlan;
    handleSelectPlan(fresh_plan);
  }, [selectedMergedPlan, merged_plan_items, handleSelectPlan]);

  useEffect(() => {
    if (!is_merged_flow) {
      return;
    }
    if (merged_day_options.length === 0) {
      if (selectedMergedDay != null) {
        setSelectedMergedDay(null);
      }
      return;
    }
    if (selectedMergedDay == null || !merged_day_options.includes(selectedMergedDay)) {
      // Initial pick: no hold. Invalid day after data/category shift: hold if collapsed.
      if (selectedMergedDay != null) {
        prepareMergedFilterTransition();
      }
      setSelectedMergedDay(merged_day_options[0]);
      setSelectedMergedPlan(null);
    }
  }, [is_merged_flow, merged_day_options, selectedMergedDay, prepareMergedFilterTransition]);

  /** Drop selection only when the current day list is non-empty and no longer contains it. */
  useEffect(() => {
    if (!is_merged_flow || selectedMergedPlan == null) {
      return;
    }
    if (merged_plan_items.length === 0) {
      return;
    }
    if (!merged_plan_items.some(item => item.key === selectedMergedPlan.key)) {
      setSelectedMergedPlan(null);
    }
  }, [is_merged_flow, merged_plan_items, selectedMergedPlan]);

  /** `all` / `flat` = same `sections` as SectionList or grid of all plans. `tag` = tag strip + category cards. */
  const mainListView = useMemo((): 'custom' | 'category' | 'merged' | 'tag' | 'flat' => {
    if (subscription_type === 'custom' && hasCustomPlans && !!show_custom_tab) {
      return 'custom';
    }
    if (subscription_list_structure === 'all') {
      return 'flat';
    }
    if (subscription_list_structure === 'merged' && is_merged_flow && (selected_category_flow ?? category_flow_categories[0])) {
      return 'merged';
    }
    if (subscription_list_structure === 'category' && is_category_flow && (selected_category_flow ?? category_flow_categories[0])) {
      return 'category';
    }
    if (subscription_list_structure === 'tag' && subscription_type === 'regular' && sections.length > 0) {
      return 'tag';
    }
    return 'flat';
  }, [
    hasCustomPlans,
    subscription_type,
    subscription_list_structure,
    is_merged_flow,
    is_category_flow,
    selected_category_flow,
    category_flow_categories,
    sections.length,
    show_custom_tab,
  ]);

  const category_flow_category_for_list = selected_category_flow ?? category_flow_categories[0] ?? null;

  const show_tag_slider = subscription_type === 'regular' && subscription_list_structure === 'tag' && tags.length > 1;
  const show_category_flow_slider =
    subscription_type === 'regular' &&
    (subscription_list_structure === 'category' || subscription_list_structure === 'merged') &&
    (is_category_flow || is_merged_flow) &&
    category_flow_categories.length > 0;
  /**
   * Sticky merged carousel + day pills only apply in regular mode.
   * Custom mode mounts its own compact slider inside the scroll body — do not reserve
   * expanded carousel height or the custom slider starts mid-page.
   */
  const use_merged_sticky_chrome = is_merged_flow && subscription_type === 'regular';
  const carousel_estimate = use_merged_sticky_chrome
    ? MERGED_CAROUSEL_EXPANDED_HEIGHT + MERGED_DAY_PILL_BAR_HEIGHT
    : show_tag_slider || show_category_flow_slider
      ? SLIDE_CARD_HEIGHT + SUBSCRIPTION_CAROUSEL_CHROME_EXTRA
      : 0;
  const filter_chrome_fallback = carousel_estimate;
  const title_row_h = androidAppHeaderContentHeight(true);
  const safe_top = androidHeaderTopInset(insets.top);
  const header_bottom_pad = appTheme.spacing.xs;
  /** Merged keeps expanded chrome padding so absolute header can shrink without fighting scroll. */
  const filter_chrome_h = use_merged_sticky_chrome
    ? MERGED_CAROUSEL_EXPANDED_HEIGHT + MERGED_DAY_PILL_BAR_HEIGHT
    : measured_filter_chrome_h > 0
      ? measured_filter_chrome_h
      : filter_chrome_fallback;
  const scroll_inset = safe_top + title_row_h + filter_chrome_h + header_bottom_pad + appTheme.spacing.md;

  const handleCustomProceedToCheckout = useCallback(
    async (payload: CustomCheckoutPayload) => {
      if (!is_logged_in) {
        await setSelectedSubscription(payload);
        openRegister({ subData: payload });
      } else {
        navigation.navigate('Checkout' as any, { plan: payload });
      }
    },
    [navigation, is_logged_in, openRegister],
  );

  const resolve_category_slide_image = useCallback((c: CategoryDaysItem) => {
    const primary = resolveAttachmentUri(c.attachment) ?? resolveAttachmentUri((c as { thumbnail?: unknown }).thumbnail);
    return primary ?? firstPlanImageFromCategoryDaysItem(c);
  }, []);

  const resolve_tag_slide_image = useCallback(
    (tag: TagChipItem) => {
      const primary = resolveAttachmentUri(tag.attachment) ?? resolveAttachmentUri((tag as { thumbnail?: unknown }).thumbnail);
      return primary ?? firstPlanImageForTagSlide(tag.id, restructuredData, regularPlans);
    },
    [restructuredData, regularPlans],
  );

  const categoryFlowSliderContent = useMemo(() => {
    if (!show_category_flow_slider || category_flow_categories.length === 0) {
      return null;
    }
    const items: Array<{ id: string; [key: string]: any }> = category_flow_categories.map((c: CategoryDaysItem) => ({
      ...c,
      id: String(c.id ?? c.title ?? ''),
    }));
    return (
      <CategorySlider
        items={items}
        getImageUri={resolve_category_slide_image}
        getTitle={(c: any) => getApiText(c as unknown as Record<string, unknown>, 'title', 'arabic_title') || c.title || ''}
        selectedId={selectedCategoryFlowId}
        onSelect={id => {
          if (is_merged_flow) {
            selectMergedCategory(id);
            return;
          }
          setSelectedCategoryFlowId(id);
        }}
        onShowBanner={handleShowBanner}
        styles={styles}
        themeColors={themeColors}
        collapseProgress={is_merged_flow ? merged_carousel_collapse_progress : undefined}
      />
    );
  }, [
    show_category_flow_slider,
    category_flow_categories,
    selectedCategoryFlowId,
    handleShowBanner,
    styles,
    themeColors,
    resolve_category_slide_image,
    is_merged_flow,
    selectMergedCategory,
    merged_carousel_collapse_progress,
  ]);

  const mergedDayPillBar = useMemo(() => {
    if (!is_merged_flow || merged_day_options.length === 0) {
      return null;
    }
    return (
      <View style={styles.mergedDayPillBar}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.mergedDayPillScrollContent}
          keyboardShouldPersistTaps="handled">
          {merged_day_options.map(day_val => {
            const is_selected = selectedMergedDay === day_val;
            return (
              <Clickable
                key={day_val}
                style={[styles.mergedDayPill, is_selected && styles.mergedDayPillActive]}
                onPress={() => selectMergedDay(day_val)}
                useNativePressable
                pressAnimated={false}
                haptics
                accessibilityLabel={`${day_val} ${t('subscriptions.daysLabel')}`}
                accessibilityRole="button"
                accessibilityState={{ selected: is_selected }}>
                <View style={styles.mergedDayPillInner}>
                  <Text style={[styles.mergedDayPillText, is_selected && styles.mergedDayPillTextActive]}>
                    {day_val} {t('subscriptions.daysLabel')}
                  </Text>
                </View>
              </Clickable>
            );
          })}
        </ScrollView>
      </View>
    );
  }, [is_merged_flow, merged_day_options, selectedMergedDay, selectMergedDay, styles, t]);
  const tagSliderContent = useMemo(() => {
    if (!show_tag_slider || tags.length <= 1) {
      return null;
    }
    return (
      <CategorySlider
        items={tags}
        getImageUri={resolve_tag_slide_image}
        getTitle={(tag: TagChipItem) => tag.name}
        selectedId={selectedTagId}
        onSelect={id => setSelectedTagId(id != null ? String(id) : null)}
        onShowBanner={handleShowBanner}
        styles={styles}
        themeColors={themeColors}
      />
    );
  }, [show_tag_slider, tags, selectedTagId, handleShowBanner, styles, themeColors, resolve_tag_slide_image]);

  const show_custom_toggle = hasCustomPlans && !!show_custom_tab;
  const is_custom_mode = subscription_type === 'custom';

  const toggle_subscription_type = useCallback(() => {
    const next: 'regular' | 'custom' = is_custom_mode ? 'regular' : 'custom';
    // Reset shared header/collapse scroll so custom (and return to regular) don't inherit mid-collapse offset.
    scrollY.value = 0;
    merged_carousel_collapse_progress.value = 0;
    merged_collapse_hold.value = 0;
    merged_restore_collapsed_ref.current = false;
    if (next === 'regular') {
      merged_scroll_ref.current?.scrollTo({ y: 0, animated: false });
    }
    setSubscriptionType(next);
  }, [
    is_custom_mode,
    scrollY,
    merged_carousel_collapse_progress,
    merged_collapse_hold,
  ]);

  const custom_plans_header_action = useMemo(() => {
    if (!show_custom_toggle) {
      return null;
    }
    return (
      <HeaderIconButton
        onPress={toggle_subscription_type}
        accessibilityLabel={is_custom_mode ? t('subscriptions.regular') : t('subscriptions.custom')}
        appearance="outline"
        shellStyle={
          is_custom_mode
            ? { backgroundColor: themeColors.primary, borderColor: themeColors.primary }
            : undefined
        }
        haptics>
        <SvgIcon
          name="edit"
          size={20}
          color={is_custom_mode ? themeColors.onPrimary ?? themeColors.primaryText : themeColors.primary}
        />
      </HeaderIconButton>
    );
  }, [show_custom_toggle, is_custom_mode, t, themeColors, toggle_subscription_type]);

  const headerBottomContent = useMemo(() => {
    const filter_content =
      subscription_type === 'regular' || !show_custom_tab
        ? is_category_flow || is_merged_flow
          ? categoryFlowSliderContent
          : tagSliderContent
        : null;
    const day_pills = use_merged_sticky_chrome ? mergedDayPillBar : null;
    if (filter_content == null && day_pills == null) {
      return null;
    }
    return (
      <View style={styles.headerBottomChrome} onLayout={on_filter_chrome_layout}>
        {filter_content}
        {day_pills}
      </View>
    );
  }, [
    subscription_type,
    is_category_flow,
    is_merged_flow,
    use_merged_sticky_chrome,
    categoryFlowSliderContent,
    tagSliderContent,
    mergedDayPillBar,
    styles,
    show_custom_tab,
    on_filter_chrome_layout,
  ]);
  const scroll_view_style = [styles.listBodyScroll];

  if (isLoading) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.container}>
          <ScreenHeader
            title={t('subscriptions.plans')}
            onBack={on_back_exit}
            rightAction={custom_plans_header_action}
            scrollY={scrollY}
          />
          <LoadingState message={t('subscriptions.loadingPlans')} />
        </View>
      </StackScreenShell>
    );
  }

  if (error) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.container}>
          <ScreenHeader
            title={t('subscriptions.plans')}
            onBack={on_back_exit}
            rightAction={custom_plans_header_action}
            scrollY={scrollY}
          />
          <ErrorState
            title={t('subscriptions.failedToLoadPlans')}
            message={t('subscriptions.tryAgain')}
            onRetry={refetch}
            error={error}
            issue_context={{
              page: t('account.browsePlans'),
              issue: 'Failed to load subscription plans',
              kind: 'api',
              api_endpoint: 'subscriptions',
              http_method: 'GET',
            }}
          />
        </View>
      </StackScreenShell>
    );
  }

  if (regularPlans.length === 0 && !hasCustomPlans) {
    return (
      <StackScreenShell colors={themeColors}>
        <View style={styles.container}>
          <ScreenHeader
            title={t('subscriptions.plans')}
            onBack={on_back_exit}
            rightAction={custom_plans_header_action}
            scrollY={scrollY}
          />
          <EmptyState icon="menu" title={t('subscriptions.noPlansAvailable')} message={t('subscriptions.noPlansMessage')} />
        </View>
      </StackScreenShell>
    );
  }

  const renderListBody = () => {
    const padding_block = { paddingTop: scroll_inset, paddingBottom: insets.bottom + appTheme.spacing.xl };
    const scroll_props = {
      contentInsetAdjustmentBehavior: 'never' as const,
      automaticallyAdjustContentInsets: false,
      keyboardShouldPersistTaps: 'handled' as const,
      nestedScrollEnabled: true,
      onScroll,
      scrollEventThrottle: 16 as const,
    };
    if (mainListView === 'custom') {
      return (
        <View style={styles.listBodyScroll}>
          <CustomPlanBuilder
            custom_plans={customPlans}
            selected_category_id={customCategoryId}
            onSelectCategory={setCustomCategoryId}
            particular_index={selectedParticularIndex}
            onSelectParticular={setSelectedParticularIndex}
            meal_counter={meal_counter}
            setMealCounter={setMealCounter}
            snack_counter={snack_counter}
            setSnackCounter={setSnackCounter}
            protein_carbs_array={protein_carbs_array}
            setProteinCarbsArray={setProteinCarbsArray}
            onProceedToCheckout={handleCustomProceedToCheckout}
            is_logged_in={is_logged_in}
            styles={styles}
            themeColors={themeColors}
            onScroll={onScroll}
            refreshing={refreshing}
            onRefresh={onRefresh}
            content_top_inset={scroll_inset}
            content_bottom_inset={insets.bottom}
          />
        </View>
      );
    }
    if (mainListView === 'category' && category_flow_category_for_list != null) {
      return (
        <Animated.ScrollView
          style={scroll_view_style as StyleProp<ViewStyle>}
          contentContainerStyle={[styles.listContent, padding_block]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={themeColors.primary} />}
          showsVerticalScrollIndicator={false}
          {...scroll_props}>
          <View style={styles.dayGrid}>
            {(category_flow_category_for_list.days ?? []).map((day_entry: CategoryDayEntry) => {
              const day_val = day_entry.day ?? day_entry.type_key ?? 0;
              return (
                <SubscriptionCatalogDayTile
                  key={day_val}
                  days={day_val}
                  days_label={t('subscriptions.daysLabel')}
                  colors={themeColors}
                  onPress={() => {
                    setCategoryFlowDayEntry(day_entry);
                    setCategoryFlowCategoryTitle(
                      getApiText(category_flow_category_for_list as unknown as Record<string, unknown>, 'title', 'arabic_title') ||
                        category_flow_category_for_list.title ||
                        '',
                    );
                    setShowCategoryFlowDayModal(true);
                  }}
                />
              );
            })}
          </View>
        </Animated.ScrollView>
      );
    }
    if (mainListView === 'merged') {
      const footer_pad = selectedMergedPlan ? 96 + insets.bottom : insets.bottom + appTheme.spacing.xl;
      /** Always allow scrolling at least COLLAPSE_DISTANCE so short lists can expand chrome again. */
      const merged_min_content_h =
        (merged_scroll_viewport_h > 0 ? merged_scroll_viewport_h : window_height) +
        MERGED_CAROUSEL_COLLAPSE_DISTANCE;
      return (
        <Animated.ScrollView
          ref={merged_scroll_ref}
          style={scroll_view_style as StyleProp<ViewStyle>}
          contentContainerStyle={[
            styles.listContent,
            {
              paddingTop: scroll_inset,
              paddingBottom: footer_pad,
              minHeight: merged_min_content_h,
            },
          ]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={themeColors.primary} />}
          showsVerticalScrollIndicator={false}
          onLayout={onMergedScrollLayout}
          onContentSizeChange={onMergedContentSizeChange}
          bounces
          alwaysBounceVertical
          {...scroll_props}>
          {merged_list_render_items.length === 0 ? (
            <Animated.View entering={FadeInDown.duration(MERGED_LIST_ENTER_MS)} exiting={FadeOutUp.duration(MERGED_LIST_EXIT_MS)}>
              <EmptyState icon="menu" title={t('subscriptions.noPlansAvailable')} message={t('subscriptions.noPlansMessage')} />
            </Animated.View>
          ) : (
            <View style={subscription_list_style === 'grid' ? styles.gridWrap : undefined}>
              {merged_list_render_items.map((item, index) => (
                <Animated.View
                  key={`${merged_list_enter_key}:${item.key}`}
                  entering={mergedListEntering(index)}
                  exiting={mergedListExiting(index)}
                  style={subscription_list_style === 'grid' ? styles.gridItemCell : { width: '100%' }}>
                  <SubscriptionCatalogPlanCard
                    item={item}
                    colors={themeColors}
                    styles={styles}
                    selected={selectedMergedPlan?.key === item.key}
                    expandable
                    onSelect={() => selectMergedPlan(item)}
                  />
                </Animated.View>
              ))}
            </View>
          )}
        </Animated.ScrollView>
      );
    }
    if (mainListView === 'tag' && sections.length > 0) {
      return (
        <Animated.ScrollView
          style={scroll_view_style as StyleProp<ViewStyle>}
          contentContainerStyle={[styles.listContent, padding_block]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={themeColors.primary} />}
          showsVerticalScrollIndicator={false}
          {...scroll_props}>
          <View style={subscription_list_style === 'grid' ? styles.gridWrap : undefined}>
            {sections.map(section => (
              <View key={section.id} style={subscription_list_style === 'grid' ? styles.gridItemCell : { width: '100%' }}>
                <SubscriptionCatalogCategoryCard section={section} colors={themeColors} styles={styles} onPress={() => openCategoryPopup(section)} />
              </View>
            ))}
          </View>
        </Animated.ScrollView>
      );
    }
    if (subscription_list_style === 'grid') {
      const flat_items = sections.flatMap(section => section.data as PlanItem[]);
      return (
        <Animated.ScrollView
          style={scroll_view_style as StyleProp<ViewStyle>}
          contentContainerStyle={[styles.listContent, padding_block]}
          refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={themeColors.primary} />}
          showsVerticalScrollIndicator={false}
          {...scroll_props}>
          <View style={styles.gridWrap}>
            {flat_items.map(item => (
              <View key={item.key} style={styles.gridItemCell}>
                <SubscriptionCatalogPlanCard item={item} colors={themeColors} styles={styles} onSelect={() => handleSelectPlan(item)} />
              </View>
            ))}
          </View>
        </Animated.ScrollView>
      );
    }
    return (
      <AnimatedSectionList
        style={scroll_view_style as StyleProp<ViewStyle>}
        sections={sections}
        keyExtractor={item => item.key}
        renderItem={renderItem}
        renderSectionHeader={renderSectionHeader}
        ListHeaderComponent={listHeader}
        contentContainerStyle={[styles.listContent, padding_block]}
        stickySectionHeadersEnabled={false}
        showsVerticalScrollIndicator={false}
        initialNumToRender={8}
        maxToRenderPerBatch={6}
        windowSize={8}
        updateCellsBatchingPeriod={50}
        removeClippedSubviews={Platform.OS === 'android'}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={themeColors.primary} />}
        {...scroll_props}
      />
    );
  };

  return (
    <StackScreenShell colors={themeColors}>
      <View style={styles.container}>
      <ScreenHeader
        title={t('subscriptions.plans')}
        onBack={on_back_exit}
        rightAction={custom_plans_header_action}
        scrollY={scrollY}
        bottomContent={headerBottomContent}
        compact
      />
      {renderListBody()}

      {mainListView === 'merged' && selectedMergedPlan ? (
        <View
          style={[styles.customStickyFooter, { paddingBottom: Math.max(insets.bottom, appTheme.spacing.md) }]}>
          <LinearGradient
            colors={[hexToRgba(themeColors.surface, 0), hexToRgba(themeColors.surface, 0.98)] as const}
            style={styles.customStickyFooterFade}
            pointerEvents="none"
          />
          <Clickable2
            style={styles.customProceedBtn}
            onPress={proceedMergedCheckout}
            haptics
            accessibilityRole="button"
            accessibilityLabel={
              !is_logged_in && !guest_explore
                ? t('subscriptions.signUpToCheckout') || t('subscriptions.proceedToCheckout')
                : t('subscriptions.proceedToCheckout')
            }>
            <Text style={[styles.customProceedPrice, ANDROID_FOOTER_TEXT]} numberOfLines={1}>
              {formatCurrency(
                resolvePlanOfferPricing(selectedMergedPlan.plan as { offer_cost?: unknown; cost?: unknown })
                  .displayPrice,
              )}
            </Text>
            <View style={[styles.customProceedInner, flipRowStyle]}>
              <Text style={[styles.customProceedText, ANDROID_FOOTER_TEXT]} numberOfLines={1}>
                {!is_logged_in && !guest_explore
                  ? t('subscriptions.signUpToCheckout') || t('subscriptions.proceedToCheckout')
                  : t('subscriptions.proceedToCheckout')}
              </Text>
              <SvgIcon name={chevronNextAlongAxis} size={14} color={themeColors.onPrimary} />
            </View>
          </Clickable2>
        </View>
      ) : null}

      <BottomSheetPopup visible={showWelcomeModal} onClose={() => setShowWelcomeModal(false)} title={t('subscriptions.welcome')} animationType="fade" showDragHandle>
        <View style={styles.welcomeSheetBody}>
          <View style={styles.welcomeIconWrap}>
            <SvgIcon name="menu" size={40} color={themeColors.primary} />
          </View>
          <Text style={styles.welcomeMessage}>{t('subscriptions.pickAPlan')}</Text>
          <Clickable style={styles.welcomeBtn} onPress={() => setShowWelcomeModal(false)}>
            <View style={styles.welcomeBtnInner}>
              <Text style={styles.welcomeBtnText}>{t('subscriptions.getStarted')}</Text>
            </View>
          </Clickable>
        </View>
      </BottomSheetPopup>

      <BottomSheetPopup
        visible={showFreeDaysModal}
        onClose={() => {
          setShowFreeDaysModal(false);
          setSelectedPlanWithFreeDays(null);
        }}
        title={t('subscriptions.freeDays')}
        animationType="slide"
        showDragHandle>
        <View style={styles.freeDaysSheetBody}>
          <Text style={styles.freeDaysMessage}>
            {t('subscriptions.addFreeDaysQuestion')}
            <Text style={styles.freeDaysHighlight}>
              {selectedPlanWithFreeDays?.free_days} {t('subscriptions.daysLabel')}
            </Text>
            {t('subscriptions.freeDaysSuffix')}
          </Text>
          <View style={styles.freeDaysOptions}>
            <Clickable style={[styles.freeDaysOption, !includeFreeDays && styles.freeDaysOptionActive]} onPress={() => setIncludeFreeDays(false)}>
              <View style={styles.freeDaysOptionInner}>
                <View style={[styles.radioOuter, !includeFreeDays && styles.radioActive]}>{!includeFreeDays && <View style={styles.radioInner} />}</View>
                <Text style={[styles.freeDaysOptionText, !includeFreeDays && styles.freeDaysOptionTextActive]}>{t('subscriptions.noThanks')}</Text>
              </View>
            </Clickable>
            <Clickable style={[styles.freeDaysOption, includeFreeDays && styles.freeDaysOptionActive]} onPress={() => setIncludeFreeDays(true)}>
              <View style={styles.freeDaysOptionInner}>
                <View style={[styles.radioOuter, includeFreeDays && styles.radioActive]}>{includeFreeDays && <View style={styles.radioInner} />}</View>
                <Text style={[styles.freeDaysOptionText, includeFreeDays && styles.freeDaysOptionTextActive]}>
                  {t('subscriptions.yesFreeDays')}
                  {selectedPlanWithFreeDays?.free_days}
                  {t('subscriptions.yesFreeDaysSuffix')}
                </Text>
              </View>
            </Clickable>
          </View>
          <Clickable style={styles.freeDaysConfirmBtn} onPress={handleConfirmFreeDays}>
            <View style={styles.freeDaysConfirmInner}>
              <Text style={styles.freeDaysConfirmText}>{t('subscriptions.continue')}</Text>
            </View>
          </Clickable>
        </View>
      </BottomSheetPopup>

      <BannerPopupModal
        visible={showBannerPopup}
        bannerData={bannerData}
        metrics={bannerMetrics}
        fallback_image_uri={banner_fallback_image_uri}
        onClose={() => {
          setShowBannerPopup(false);
          setBannerData(null);
          setBannerMetrics(null);
          setBannerFallbackImageUri(null);
        }}
        styles={styles}
        themeColors={themeColors}
      />

      <CategoryPlanModal
        visible={showCategoryPopup}
        section={categoryPopupSection}
        selectedDay={selectedDayForPopup}
        onSelectDay={setSelectedDayForPopup}
        onSelectPlan={handleSelectPlanFromCategoryModal}
        onClose={close_category_plan_modal}
        onDismissComplete={on_category_plan_dismiss_complete}
        styles={styles}
        themeColors={themeColors}
      />

      <CategoryFlowDayPlansModal
        visible={showCategoryFlowDayModal}
        dayEntry={categoryFlowDayEntry}
        categoryTitle={categoryFlowCategoryTitle}
        onSelectPlan={handleSelectPlanFromCategoryFlowModal}
        onClose={close_category_flow_day_modal}
        onDismissComplete={on_category_flow_day_dismiss_complete}
        styles={styles}
        themeColors={themeColors}
      />
      </View>
    </StackScreenShell>
  );
};

export default SubscriptionListScreen;
